import React, { useEffect, useMemo, useState } from "react";
import { Card, Tab, Tabs } from "react-bootstrap";
import { ThemeProviderV2, IconButton} from '@blueprint/ui';
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import profileApi, {
    UserProfile
} from "../../api/profile";
import { Loader } from "../../common/components/core";
import { isNodeDeveloper } from "../../utils/role";

import { ProfileTable } from "./components/ProfileTable";
import { Profile } from "./ProfileDetailView";
import { PageTitle } from "../../types/pageTitle";
import { ResponseWithPagination } from "../../types/common";
export enum ProfileType {
    ALL = "All_PROFILES",
    DEVELOPER = "DEVELOPER",
    APPROVER = "APPROVER",
    HSBC = "HSBC",
    MAIN_CONTRACTOR = "MAIN_CONTRACTOR",
}

export const generateProfileLocationDisplayName = (
    location: string,
): string => {
    return location === "HK" ? "Hong Kong" : location;
};

export const generateTypeDisplayName = (type: ProfileType | string): string => {
    switch (type) {
        case ProfileType.ALL:
            return "All Profiles";
        case ProfileType.DEVELOPER:
            return "Developer";
        case ProfileType.APPROVER:
            return "Approver";
        case ProfileType.HSBC:
            return "HSBC";
        case ProfileType.MAIN_CONTRACTOR:
            return "Service Provider";
        default:
            return type;
    }
};

export const transformToProfileArray = (profiles: UserProfile[]): Profile[] =>
    profiles.map((profile) => {
        return {
            id: profile.id || undefined,
            profileName: profile.name,
            profileType: profile.type,
            profileCountryCity: profile.location,
            contactPersonName: profile.contactPersonName,
            contactPersonNumber: profile.contactPersonNumber,
            contactPersonEmail: profile.contactPersonEmail,
        };
    });

export const transformToProfileArrayPaging = (profiles: ResponseWithPagination<UserProfile[]>): ResponseWithPagination<Profile[]> => {
    let formatProfilesContent: Profile[] = profiles.content.map((profile) => {
        return {
            id: profile.id || undefined,
            profileName: profile.name,
            profileType: profile.type,
            profileCountryCity: profile.location,
            contactPersonName: profile.contactPersonName,
            contactPersonNumber: profile.contactPersonNumber,
            contactPersonEmail: profile.contactPersonEmail,
        };
    });

    let formatProfiles: ResponseWithPagination<Profile[]> = {
        content: formatProfilesContent,
        pageable: profiles.pageable,
        last: profiles.last,
        totalPages: profiles.totalPages,
        totalElements: profiles.totalElements,
        first: profiles.first,
        numberOfElements: profiles.numberOfElements,
        size: profiles.size,
        number: profiles.number,
        sort: profiles.sort,
        empty: profiles.empty
    }
    return formatProfiles;
};

    
export type FilterTypetValues = {
    type: string;
    pageNo: number;
};

const StyledTabs = styled(Tabs)`
    &.nav-tabs {
        border-bottom: none !important;
    }
    padding-top: 24px;
    padding-bottom: 32px;
`;

const sortedProfiles = {
    developer: [
        ProfileType.ALL,
        ProfileType.DEVELOPER,
        ProfileType.APPROVER,
        ProfileType.HSBC,
    ],
    contractor: [
        ProfileType.ALL,
        ProfileType.MAIN_CONTRACTOR,
        ProfileType.HSBC,
    ],
};

const ProfileManageMentView = (): JSX.Element => {
    const [typeValue, setTypeValue] = useState<FilterTypetValues>({type: ProfileType.ALL, pageNo: 0});
    const pageSize = 10;

    document.title = PageTitle.ProfileManageMentView;


    const [profiles, setProfiles] = useState<ResponseWithPagination<UserProfile[]>>();
    const [isProfileListLoading, setProfileListLoading] = useState(false);

    useEffect(() => {
        const getProfileList = async () => {
            try {
                setProfileListLoading(true);
                let profiles = null
                if(typeValue.type === ProfileType.ALL) {
                    profiles = await profileApi.profileListPaging(typeValue.pageNo, pageSize);
                } else{
                    profiles = await profileApi.profileListPagingByType(typeValue.type ,typeValue.pageNo, pageSize);
                }
                setProfiles(profiles)
                setProfileListLoading(false);
            } catch (e) {
                console.error(e);
                setProfileListLoading(false);
            }
            return;
        };

        getProfileList();
    }, [typeValue]);

    const selectType = (type: string) => {
        setTypeValue({type: type, pageNo: 0})
    }

    const nextPage = () => {
        if (profiles && !profiles.last) {
            // setPageNo(pageNo + 1);
            setTypeValue((olddata)=>{
                return {
                    ...olddata,
                    pageNo: olddata.pageNo + 1
                }
            })
        }
    };
    const prevPage = () => {
        if (typeValue.pageNo > 0) {
            setTypeValue((olddata)=>{
                return {
                    ...olddata,
                    pageNo: olddata.pageNo - 1
                }
            })
            // setPageNo(pageNo - 1);
        }
    };
    const toPage = (page: number) => {
        if (profiles && page !== profiles.number) {
            if (page >= 0 && page < profiles.totalPages) {
                // setPageNo(page);
                setTypeValue((olddata)=>{
                    return {
                        ...olddata,
                        pageNo: page
                    }
                })
            }
        }
    };
    return (
        <ThemeProviderV2>
        <Loader isLoading={isProfileListLoading}>
            {profiles && (
                <PureProfileManageMentView
                    profiles={transformToProfileArrayPaging(profiles)}
                    type={typeValue.type}
                    selectType={selectType}
                    nextPage={nextPage}
                    prevPage={prevPage}
                    toPage={toPage}
                />
            )}
        </Loader>
        </ThemeProviderV2>
    );
};

type PureProfileManageMentViewProps = {
    profiles: ResponseWithPagination<Profile[]>;
    type: string
    selectType: (type: string) => void;
    nextPage: () => void;
    prevPage: () => void;
    toPage: (page: number) => void;
};

export const PureProfileManageMentView = ({
    profiles,
    type,
    selectType,
    nextPage,
    prevPage,
    toPage,
}: PureProfileManageMentViewProps): JSX.Element => {
    const history = useHistory();
    const [, setKey] = useState<string>(ProfileType.ALL);

    const profileTabs = useMemo(
        () =>
            isNodeDeveloper()
                ? sortedProfiles.developer
                : sortedProfiles.contractor,
        [],
    );
    return (
        <>
            <div className="d-flex align-items-center mb-4">
                <h1 className="flex-grow-1 mb-0">Profile Management</h1>
                <IconButton
                    icon="Add"
                    variant="light.primary"
                    onClick={() =>
                        history.push("/profile-management/create-new-profile")
                    }
                >
                    Create Profile
                </IconButton>
            </div>
            <Card className="mt-4 border-0">
                <Card.Body className="px-0 py-0">
                    <StyledTabs
                        defaultActiveKey={ProfileType.ALL}
                        activeKey={type}
                        onSelect={(k) => {
                            k && setKey(k);
                            k && selectType(k);
                            // TODO call back to refresh data
                        }}
                    >
                        {profileTabs.map((type, index) => (
                            <Tab
                                eventKey={type}
                                title={generateTypeDisplayName(type)}
                                key={index}
                            >
                                <ProfileTable 
                                    profiles={profiles}
                                    nextPage={nextPage}
                                    prevPage={prevPage}
                                    toPage={toPage} />
                            </Tab>
                        ))}
                    </StyledTabs>
                </Card.Body>
            </Card>
        </>
    );
};

export default ProfileManageMentView;
